// ============================================================================
// BP-13-02: Admin Users Hooks
// BrightPath Platform - Admin Frontend
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminUsersApi } from '@/features/admin/api/adminApi';
import type {
  AdminUserListParams,
  UpdateAdminUser,
  SuspendUserRequest,
  UserActivityListParams,
} from '@/types/admin';
import { useToast } from '@/core/hooks/useToast';

// =============================================================================
// Query Keys
// =============================================================================

export const adminUserKeys = {
  all: ['admin', 'users'] as const,
  lists: () => [...adminUserKeys.all, 'list'] as const,
  list: (params: AdminUserListParams) => [...adminUserKeys.lists(), params] as const,
  details: () => [...adminUserKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminUserKeys.details(), id] as const,
  activity: (id: string, params: UserActivityListParams) =>
    [...adminUserKeys.detail(id), 'activity', params] as const,
  stats: () => [...adminUserKeys.all, 'stats'] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch paginated list of users
 */
export function useAdminUsers(params: AdminUserListParams) {
  return useQuery({
    queryKey: adminUserKeys.list(params),
    queryFn: () => adminUsersApi.list(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch a single user's details
 */
export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: adminUserKeys.detail(userId),
    queryFn: () => adminUsersApi.get(userId),
    enabled: !!userId,
  });
}

/**
 * Fetch user activity log
 */
export function useUserActivity(userId: string, params: UserActivityListParams) {
  return useQuery({
    queryKey: adminUserKeys.activity(userId, params),
    queryFn: () => adminUsersApi.getActivity(userId, params),
    enabled: !!userId,
  });
}

/**
 * Fetch user statistics
 */
export function useUserStats() {
  return useQuery({
    queryKey: adminUserKeys.stats(),
    queryFn: () => adminUsersApi.getStats(),
    staleTime: 60 * 1000, // 1 minute
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Update user details
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateAdminUser }) =>
      adminUsersApi.update(userId, data),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.setQueryData(adminUserKeys.detail(user.id), user);
      toast({
        title: 'User updated',
        description: `${user.fullName} has been updated successfully.`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Suspend a user
 */
export function useSuspendUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: SuspendUserRequest }) =>
      adminUsersApi.suspend(userId, data),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.setQueryData(adminUserKeys.detail(user.id), user);
      queryClient.invalidateQueries({ queryKey: adminUserKeys.stats() });
      toast({
        title: 'User suspended',
        description: `${user.fullName} has been suspended.`,
        variant: 'warning',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Suspension failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Activate a suspended user
 */
export function useActivateUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => adminUsersApi.activate(userId),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.setQueryData(adminUserKeys.detail(user.id), user);
      queryClient.invalidateQueries({ queryKey: adminUserKeys.stats() });
      toast({
        title: 'User activated',
        description: `${user.fullName} has been reactivated.`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Activation failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => adminUsersApi.delete(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.removeQueries({ queryKey: adminUserKeys.detail(userId) });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.stats() });
      toast({
        title: 'User deleted',
        description: 'The user has been deleted successfully.',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Delete failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Reset user password
 */
export function useResetUserPassword() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => adminUsersApi.resetPassword(userId),
    onSuccess: () => {
      toast({
        title: 'Password reset email sent',
        description: 'The user will receive an email with instructions to reset their password.',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Password reset failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Export users to CSV
 */
export function useExportUsers() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (params: AdminUserListParams) => adminUsersApi.export(params),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'Export complete',
        description: 'Users have been exported to CSV.',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Export failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Impersonate user (admin debugging)
 */
export function useImpersonateUser() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => adminUsersApi.impersonate(userId),
    onSuccess: ({ token }) => {
      // Store impersonation token and redirect
      sessionStorage.setItem('impersonation_token', token);
      sessionStorage.setItem('original_token', localStorage.getItem('auth_token') || '');
      toast({
        title: 'Impersonation started',
        description: 'You are now viewing the platform as this user. Click "End Impersonation" to return.',
        variant: 'info',
      });
      window.location.href = '/family';
    },
    onError: (error: Error) => {
      toast({
        title: 'Impersonation failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}


